// System-сессия Story 1.6: правдивые состояния команд владельца
// (ui-contract.md §2–§3, UX-DR35/51). Story 1.7 / FR-4: вместо русского текста
// модуль возвращает семантические locale-ключи — язык выбирает `locale.js`, а
// рендерит `t(key)` в текущем locale, поэтому смена языка не оставляет
// сообщение прежнего языка.
//
// Модуль не является вторым state-owner: он держит только безопасные ключи и
// гейт «подтверждённого успеха». Владельцем epoch/cleanup остаётся `app.js`,
// транспортом — `api.js`.

/** Успех «Завершить все сессии»: подтверждён сервером, текущая сессия тоже отозвана. */
export const SESSION_ENDED_NOTICE = "notice.sessionEnded";
/** Успех смены пароля: прежний пароль недействителен, все сеансы завершены. */
export const PASSWORD_CHANGED_NOTICE = "notice.passwordChanged";

/**
 * Точный контракт успеха: тело `AuthSessionResponse::anonymous()`.
 * `authenticated === false` и **явный** `username: null`; неразбираемое/пустое
 * тело, `authenticated: true` и непустой username успехом не являются.
 */
export function isConfirmedAnonymous(payload) {
  return (
    payload !== null &&
    typeof payload === "object" &&
    payload.authenticated === false &&
    payload.username === null
  );
}

/**
 * Подтверждённый успех команды: ровно HTTP 200 **и** parseable anonymous-тело.
 * 201/204/любой иной 2xx, неразбираемое тело и тело с `authenticated: true`
 * успехом не считаются.
 */
export async function isConfirmedCommandResponse(response) {
  if (response.status !== 200) return false;
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/json")) return false;
  try {
    return isConfirmedAnonymous(await response.clone().json());
  } catch (_error) {
    return false;
  }
}

/**
 * Правдивый locale-ключ по коду отказа. Ни одно сообщение не повторяет введённые
 * значения и не выдаёт отказ за успех; ambiguous-исход (503/abort) прямо говорит
 * о неизвестном результате, но и не обещает откат.
 */
export function commandFailureNotice(code, command) {
  switch (code) {
    case "invalid_credentials":
      // Локальный отказ: сессия остаётся действительной.
      return "error.command.invalid_credentials";
    case "password_unchanged":
      return "error.command.password_unchanged";
    case "password_policy_violation":
      return "error.command.password_policy_violation";
    case "invalid_password_change_request":
      return "error.command.invalid_password_change_request";
    case "invalid_session_command_request":
      return "error.command.invalid_session_command_request";
    case "unsupported_media_type":
      return "error.command.unsupported_media_type";
    case "csrf_rejected":
      return "error.command.csrf_rejected";
    case "auth_overload":
      return "error.command.auth_overload";
    case "owner_store_unavailable":
    case "auth_clock_invalid":
    case "network":
      return command === "password-change" || command === "end-all"
        ? "error.command.unknown_commit"
        : "error.command.unavailable";
    case "aborted":
      return "error.command.aborted";
    case "authentication_required":
      return "error.command.authentication_required";
    case "unconfirmed":
      // Неподтверждённый ответ не доказывает ни успеха, ни отсутствия
      // изменений: сервер мог commit-нуть команду. Поэтому сообщение
      // говорит о неизвестном результате, а не обещает no-write/rollback.
      return "error.command.unconfirmed";
    default:
      return "error.command.generic";
  }
}

/** Код отказа из TC-1 envelope; отсутствующий/неразбираемый → централизованный путь. */
export function envelopeCode(payload) {
  if (payload === null || typeof payload !== "object") return "";
  return typeof payload.code === "string" ? payload.code : "";
}

/** Безопасное чтение кода ошибки из ответа (без эха тела). */
export async function responseErrorCode(response) {
  try {
    return envelopeCode(await response.clone().json());
  } catch (_error) {
    return "";
  }
}
